import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import { Close, History, ReportOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'
import { Box, IconButton, Stack, useMediaQuery, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'

// Hooks
import { useIncidenceLoader } from './Incidence/hooks/useIncidenceLoader'
import { useIncidenceCatalog } from './Incidence/hooks/useIncidenceCatalog'
import { useIncidenceActions } from './Incidence/hooks/useIncidenceActions'
import { useIncidenceHistory } from './Incidence/hooks/useIncidenceHistory'

// Components
import IncidenceStatusCard from './Incidence/components/IncidenceStatusCard'
import IncidenceDetailDialog from './Incidence/components/IncidenceDetailDialog'
import IncidenceCreateDialog from './Incidence/components/IncidenceCreateDialog'
import IncidenceHistoryColumn from './Incidence/components/IncidenceHistoryColumn'
import IncidenceHistoryDetailDialog from './Incidence/components/IncidenceHistoryDetailDialog'
import theme from '../../../../themeConfig'

/**
 * Widget to manage device incidences.
 * Maintains the state of active incidences, allows creating incidences and opening/closing the detail dialog.
 *
 * @returns {JSX.Element}
 */
/**
 * Comparator function to sort incidences by name case-insensitively.
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
const sortByNameInsensitive = (a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' })

/**
 * Builds the view model consumed by the incidence detail dialog.
 * @param {object|null} incidence Active incidence entity.
 * @param {Function} formatDate Date formatting function.
 * @param {Function} formatTime Time formatting function.
 * @returns {object|null}
 */
export const buildIncidenceViewModel = (incidence, formatDate, formatTime) => {
  if (!incidence) return null

  return {
    startDate: formatDate(incidence.startDate),
    startHour: formatTime(incidence.startDate),
    incidenceCode: incidence.incidence?.code ?? '-',
    incidenceName: incidence.incidence?.name ?? '-',
    incidenceDescription: incidence.incidence?.description ?? '-',
    comment: incidence.comment ?? '-',
    status: incidence.incidence?.status?.name ?? '-', // system-value: raw-ok
    // Story 204 — S74: the status row itself, so the dialog can show it by code (RN2.1).
    statusValue: incidence.incidence?.status || {},
    createdBy: incidence.createdBy?.code ?? '-'
  }
}

/**
 * Normalizes a free-text search input for case-insensitive matching.
 * @param {string} value
 * @returns {string}
 */
const normalizeSearchText = (value) => String(value || '').trim().toLowerCase()

const IncidenceWidget = (props) => {
  const { w } = props
  const { formatDate, formatTime } = useDateFormatter()
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const isTouchLayout = useMediaQuery(theme.breakpoints.down('md'))
  const widgetWidth = Number(w ?? 0)
  const showHistoryColumn = widgetWidth >= 3
  const isHistoryCompact = widgetWidth === 3
  const canShowHistoryOverlay = widgetWidth === 2 || isHistoryCompact

  // State
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showHistoryDetailDialog, setShowHistoryDetailDialog] = useState(false)
  const [selectedHistoryIncidence, setSelectedHistoryIncidence] = useState(null)
  const [isHistoryEditing, setIsHistoryEditing] = useState(false)
  const [historyComment, setHistoryComment] = useState('')
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const historyOverlayRef = useRef(null)

  // Form State
  const [selectedTypeName, setSelectedTypeName] = useState('')
  const [selectedIncidenceName, setSelectedIncidenceName] = useState('')
  const [comment, setComment] = useState('')
  const [selectedIncidenceObj, setSelectedIncidenceObj] = useState(null)
  const [searchText, setSearchText] = useState('')

  // Hooks
  const { incidence, loadIncidence } = useIncidenceLoader()
  const {
    catalog,
    loading: catalogLoading,
    error: catalogError,
    loadCatalog,
    incidenceTypes,
    getIncidencesByType
  } = useIncidenceCatalog()

  const {
    createIncidence,
    finishIncidence,
    updateIncidence,
    saving,
    error: actionError,
    setError: setActionError
  } = useIncidenceActions(loadIncidence)

  const {
    incidences: incidenceHistory,
    loading: historyLoading,
    error: historyError,
    reload: reloadHistory
  } = useIncidenceHistory({ enabled: showHistoryColumn || historyExpanded })

  // Computed
  const hasIncidence = !!incidence

  const viewModel = useMemo(() => {
    return buildIncidenceViewModel(incidence, formatDate, formatTime)
  }, [incidence, formatDate, formatTime])

  const incidencesByType = useMemo(() => getIncidencesByType(selectedTypeName), [selectedTypeName, getIncidencesByType])
  const incidenceOptions = useMemo(() => incidencesByType.map((inc) => inc?.name).filter(Boolean), [incidencesByType])

  const incidenceGroups = useMemo(() => {
    const sortedTypes = [...(incidenceTypes || [])].filter((incidenceType) => incidenceType?.name).sort(sortByNameInsensitive) // system-value: raw-ok

    return sortedTypes.map((incidenceType) => {
      const items = getIncidencesByType(incidenceType.name) // system-value: raw-ok
        .filter((incidenceItem) => incidenceItem?.id && incidenceItem?.name)
        .sort(sortByNameInsensitive)
      return { type: incidenceType, items }
    }).filter((g) => g.items.length > 0)
  }, [incidenceTypes, getIncidencesByType])

  const normalizedSearch = useMemo(() => normalizeSearchText(searchText), [searchText])

  const visibleIncidenceGroups = useMemo(() => {
    if (!incidenceGroups.length) return []
    if (!normalizedSearch) return incidenceGroups.map((g) => ({ ...g, filteredItems: g.items }))

    return incidenceGroups
      .map(({ type, items }) => {
        const filteredItems = items.filter((incidenceItem) => {
          const haystack = `${incidenceItem?.name ?? ''} ${incidenceItem?.code ?? ''} ${incidenceItem?.description ?? ''}`.toLowerCase()
          return haystack.includes(normalizedSearch)
        })
        return { type, items, filteredItems }
      })
      .filter((g) => g.filteredItems.length > 0)
  }, [incidenceGroups, normalizedSearch])

  const displayedError = actionError || catalogError
  const displayedErrorMessage =
    typeof displayedError === 'string'
      ? displayedError
      : displayedError?.message || t_('An error occurred')

  // Effects
  useEffect(() => {
    if (showCreateDialog || isEditing) {
      loadCatalog()
    }
  }, [showCreateDialog, isEditing, loadCatalog])

  useEffect(() => {
    if (showCreateDialog) {
      // Reset form when opening create dialog
      setSelectedTypeName('')
      setSelectedIncidenceName('')
      setComment('')
      setSelectedIncidenceObj(null)
      setSearchText('')
      setActionError(null)
    }
  }, [showCreateDialog, setActionError])

  useEffect(() => {
    if (!canShowHistoryOverlay) {
      setHistoryExpanded(false)
    }
  }, [canShowHistoryOverlay])

  useEffect(() => {
    if (!historyExpanded) return
    const id = window.requestAnimationFrame(() => historyOverlayRef.current?.focus?.())
    return () => window.cancelAnimationFrame(id)
  }, [historyExpanded])

  // Handlers
  /**
   * Creates a new incidence for the selected catalog item and closes the create dialog on success.
   * @returns {Promise<void>}
   */
  const handleCreate = useCallback(async () => {
    if (!selectedIncidenceObj) return
    const success = await createIncidence(selectedIncidenceObj, comment)
    if (success) {
      setShowCreateDialog(false)
      reloadHistory({ showLoading: false })
    }
  }, [comment, createIncidence, reloadHistory, selectedIncidenceObj])

  /**
   * Finishes the active incidence and closes the detail dialog on success.
   * @returns {Promise<void>}
   */
  const handleFinish = useCallback(async () => {
    const success = await finishIncidence(incidence)
    if (success) {
      setShowDetailDialog(false)
      reloadHistory({ showLoading: false })
    }
  }, [finishIncidence, incidence, reloadHistory])

  /**
   * Switches the detail dialog into edit mode and seeds the form state from the active incidence.
   * @returns {void}
   */
  const handleStartModify = useCallback(() => {
    if (!incidence) return
    setSelectedTypeName(incidence?.incidence?.incidenceType?.name ?? '') // system-value: raw-ok
    setSelectedIncidenceName(incidence?.incidence?.name ?? incidence?.incidence?.code ?? '')
    setComment(incidence?.comment ?? '')
    setActionError(null)
    setIsEditing(true)
  }, [incidence, setActionError])

  /**
   * Persists modifications to the active incidence and exits edit mode on success.
   * @returns {Promise<void>}
   */
  const handleSaveModify = useCallback(async () => {
    const success = await updateIncidence(incidence, comment)
    if (success) {
      setIsEditing(false)
      reloadHistory({ showLoading: false })
    }
  }, [comment, incidence, reloadHistory, updateIncidence])

  /**
   * Closes the incidence detail dialog and clears edit state/errors.
   * @returns {void}
   */
  const handleCloseDetail = useCallback(() => {
    setShowDetailDialog(false)
    setIsEditing(false)
    setActionError(null)
  }, [setActionError])

  /**
   * Opens the create incidence dialog.
   * @returns {void}
   */
  const openCreateDialog = useCallback(() => setShowCreateDialog(true), [])

  /**
   * Opens the incidence detail dialog.
   * @returns {void}
   */
  const openDetailDialog = useCallback(() => setShowDetailDialog(true), [])

  const handleCloseCreate = useCallback(() => setShowCreateDialog(false), [])

  const handleOpenHistoryDetail = useCallback((historyIncidence) => {
    if (!historyIncidence) return
    setSelectedHistoryIncidence(historyIncidence)
    setIsHistoryEditing(false)
    setHistoryComment(historyIncidence?.comment ?? '')
    setActionError(null)
    setShowHistoryDetailDialog(true)
    setHistoryExpanded(false)
  }, [setActionError])

  const handleCloseHistoryDetail = useCallback(() => {
    setShowHistoryDetailDialog(false)
    setSelectedHistoryIncidence(null)
    setIsHistoryEditing(false)
    setHistoryComment('')
    setActionError(null)
  }, [setActionError])

  const handleStartHistoryModify = useCallback(() => {
    if (!selectedHistoryIncidence) return
    setHistoryComment(selectedHistoryIncidence?.comment ?? '')
    setActionError(null)
    setIsHistoryEditing(true)
  }, [selectedHistoryIncidence, setActionError])

  const handleCancelHistoryModify = useCallback(() => {
    setIsHistoryEditing(false)
    setActionError(null)
  }, [setActionError])

  const handleSaveHistoryModify = useCallback(async () => {
    const success = await updateIncidence(selectedHistoryIncidence, historyComment)
    if (success) {
      setSelectedHistoryIncidence((prev) => prev ? { ...prev, comment: historyComment } : prev)
      setIsHistoryEditing(false)
      reloadHistory({ showLoading: false })
    }
  }, [historyComment, reloadHistory, selectedHistoryIncidence, updateIncidence])

  const handleToggleHistoryExpanded = useCallback(() => {
    setHistoryExpanded((prev) => !prev)
  }, [])

  const handleHistoryOverlayKeyDown = useCallback((e) => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    setHistoryExpanded(false)
  }, [])

  const handleSelectIncidence = useCallback((inc, typeName) => {
    setSelectedIncidenceObj(inc)
    setSelectedTypeName(typeName)
    setSelectedIncidenceName(inc?.name || '')
    setActionError(null)
  }, [setActionError])

  return (
    <>
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        {widgetWidth === 2 && !historyExpanded
          ? (
            <IconButton
              onClick={handleToggleHistoryExpanded}
              aria-label={t_('Show incidence history')}
              size='small'
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 12,
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.85),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.background.paper, 0.95)
                }
              }}
            >
              <History fontSize='small' />
            </IconButton>
            )
          : null}
        <Stack direction='row' spacing={isHistoryCompact ? 1.25 : 2} sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
            <IncidenceStatusCard
              hasIncidence={hasIncidence}
              incidenceName={viewModel?.incidenceName}
              startTime={viewModel?.startHour}
              onCreateClick={openCreateDialog}
              onDetailClick={openDetailDialog}
              onCloseClick={handleFinish}
              closing={saving}
              compact={widgetWidth === 2}
            />
          </Box>
          {showHistoryColumn
            ? (
              <Box sx={{ height: '100%', flex: '0 0 auto' }}>
                <IncidenceHistoryColumn
                  incidences={incidenceHistory}
                  loading={historyLoading}
                  error={historyError}
                  onSelect={handleOpenHistoryDetail}
                  selectedIncidenceId={selectedHistoryIncidence?.id}
                  compact={isHistoryCompact}
                  expanded={!isHistoryCompact}
                  onToggleExpanded={isHistoryCompact ? handleToggleHistoryExpanded : undefined}
                />
              </Box>
              )
            : null}
        </Stack>

        {canShowHistoryOverlay && historyExpanded
          ? (
            <Box
              ref={historyOverlayRef}
              onClick={handleToggleHistoryExpanded}
              onKeyDown={handleHistoryOverlayKeyDown}
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: widgetWidth === 2 ? 'center' : 'flex-end',
                p: widgetWidth === 2 ? 1.25 : 1,
                bgcolor: alpha(theme.palette.background.default, 0.55),
                backdropFilter: 'blur(6px)'
              }}
              tabIndex={-1}
              aria-label={t_('Expanded history')}
              role='dialog'
              aria-modal='true'
            >
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  height: '100%',
                  width: widgetWidth === 2 ? 'min(520px, calc(100% - 20px))' : 'min(420px, calc(100% - 12px))'
                }}
              >
                <IncidenceHistoryColumn
                  incidences={incidenceHistory}
                  loading={historyLoading}
                  error={historyError}
                  onSelect={handleOpenHistoryDetail}
                  selectedIncidenceId={selectedHistoryIncidence?.id}
                  compact={false}
                  expanded
                  headerAction={{
                    icon: <Close fontSize='small' />,
                    ariaLabel: t_('Close history'),
                    onClick: handleToggleHistoryExpanded
                  }}
                  fullWidth
                  elevation={12}
                  cardSx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.96)
                  }}
                />
              </Box>
            </Box>
            )
          : null}
      </Box>

      {/* CREATE DIALOG */}
      <IncidenceCreateDialog
        open={showCreateDialog}
        onClose={handleCloseCreate}
        isTouchLayout={isTouchLayout}
        saving={saving}
        catalogLoading={catalogLoading}
        catalog={catalog}
        incidenceGroups={incidenceGroups}
        visibleIncidenceGroups={visibleIncidenceGroups}
        normalizedSearch={normalizedSearch}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        selectedTypeName={selectedTypeName}
        selectedIncidenceName={selectedIncidenceName}
        selectedIncidenceObj={selectedIncidenceObj}
        onSelectIncidence={handleSelectIncidence}
        comment={comment}
        onCommentChange={setComment}
        displayedError={displayedError}
        displayedErrorMessage={displayedErrorMessage}
        onCreate={handleCreate}
      />

      {/* DETAIL DIALOG */}
      <IncidenceDetailDialog
        open={showDetailDialog}
        onClose={handleCloseDetail}
        viewModel={viewModel}
        isEditing={isEditing}
        onStartModify={handleStartModify}
        onCancelModify={() => setIsEditing(false)}
        onSaveModify={handleSaveModify}
        onFinish={handleFinish}
        finishSaving={saving}
        modifySaving={saving}
        modifyError={actionError}
        formProps={{
          loading: catalogLoading,
          incidenceTypeOptions: incidenceTypes.map(t => t?.name).filter(Boolean),
          selectedTypeName,
          onTypeChange: setSelectedTypeName,
          incidenceOptions,
          selectedIncidenceName,
          onIncidenceChange: setSelectedIncidenceName,
          comment,
          onCommentChange: setComment,
          disableType: true,
          disableIncidence: true
        }}
      />

      <IncidenceHistoryDetailDialog
        open={showHistoryDetailDialog}
        onClose={handleCloseHistoryDetail}
        incidence={selectedHistoryIncidence}
        isEditing={isHistoryEditing}
        onStartModify={handleStartHistoryModify}
        onCancelModify={handleCancelHistoryModify}
        onSaveModify={handleSaveHistoryModify}
        saving={saving}
        error={actionError}
        comment={historyComment}
        onCommentChange={setHistoryComment}
      />
    </>
  )
}

class IncidenceWidgetProvider extends BaseWidgetProvider {
  /**
   * Unique code for the widget.
   * @returns {string}
   */
  getCode = () => 'IncidenceWidgetProvider'

  /**
   * Visible name of the widget.
   * @returns {string}
   */
  getName = () => 'Incidence'

  /**
   * Icon component for the widget.
   * @returns {import('react').ComponentType}
   */
  getIcon = () => ReportOutlined

  /**
   * Status of the widget in the grid based on global data.
   * @param {object} globalData
   * @returns {string|null}
   */
  getStatus (globalData) {
    return globalData?.incidence ? 'error' : null
  }

  /**
   * Renders the widget.
   * @param {object} props
   * @returns {JSX.Element}
   */
  createWidget = (props) => <IncidenceWidget {...props} />

  createPreviewWidget = () => {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', gap: 10, flex: 1, overflow: 'hidden' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ flex: 1, background: '#4caf7a', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span>✓</span>
            </div>
            <span>No active incidences</span>
          </div>
          <div style={{ background: theme.palette.red.main, borderRadius: 10, padding: '6px 10px', textAlign: 'center' }}>
            <span>Create Incidence</span>
          </div>
        </div>
        <div style={{ flex: 1, background: theme.palette.background.default, borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <p>History</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, overflow: 'hidden' }}>
            <div style={{ background: theme.palette.background.paper, borderRadius: 6, flex: 1 }} />
            <div style={{ background: theme.palette.background.paper, borderRadius: 6, flex: 1 }} />
            <div style={{ background: theme.palette.background.paper, borderRadius: 6, flex: 1 }} />
          </div>
        </div>
      </div>
    )
  }

  size = { defaultW: 2, defaultH: 4, minW: 2, minH: 4 }

  /**
   * Widget properties for GridStack.
   * @param {number} columns
   * @param {number} cellHeight
   * @returns {object}
   */
  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size) }
  }
}

export default IncidenceWidgetProvider
